/**
 * Lightweight collapse primitives for the summary view.
 *
 * `TravelCollapsibleSection` wraps a page-level region (Budget, Map,
 * Dossier, Itinerary) with a clickable header that shows a chevron and
 * a subtle right-aligned meta slot. It owns its own open/closed state so
 * the summary view can stay declarative.
 *
 * `useCollapsibleSet` is a small hook for the dossier's per-section
 * expansion — any number of sections, each tracked independently.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface TravelCollapsibleSectionProps {
  title: ReactNode;
  /** Small right-aligned meta text (count, last-updated, etc.) */
  meta?: ReactNode;
  /** Start open (default) or closed. */
  defaultOpen?: boolean;
  /** Left-side icon (lucide component). */
  icon?: ReactNode;
  /** Optional accent element rendered even when collapsed (e.g. a summary chip). */
  collapsedPreview?: ReactNode;
  children: ReactNode;
}

export function TravelCollapsibleSection({
  title,
  meta,
  defaultOpen = true,
  icon,
  collapsedPreview,
  children,
}: TravelCollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <section className="rounded-lg border border-border bg-card/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-muted/30 rounded-t-lg"
        aria-expanded={open}
      >
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
        {icon && <span className="shrink-0 text-muted-foreground">{icon}</span>}
        <span className="flex-1 font-semibold text-sm">{title}</span>
        {meta && <span className="text-xs text-muted-foreground shrink-0">{meta}</span>}
      </button>
      {!open && collapsedPreview && (
        <div className="px-4 pb-3 -mt-1 text-xs text-muted-foreground">{collapsedPreview}</div>
      )}
      {open && <div className="border-t border-border p-4">{children}</div>}
    </section>
  );
}

/**
 * Tracks which of a set of string keys are currently open. The key list can
 * change between renders — missing keys default to `defaultOpen`.
 */
export function useCollapsibleSet(_keys: string[], defaultOpen: boolean) {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const isOpen = useCallback(
    (key: string) => {
      if (key in overrides) return overrides[key];
      return defaultOpen;
    },
    [overrides, defaultOpen],
  );

  const toggle = useCallback(
    (key: string) => {
      setOverrides((prev) => ({
        ...prev,
        [key]: !(key in prev ? prev[key] : defaultOpen),
      }));
    },
    [defaultOpen],
  );

  return { isOpen, toggle };
}
